import { booleanAttribute, inject } from '@angular/core';
import { type ActivatedRouteSnapshot, type CanActivateFn, type ResolveFn, Router } from '@angular/router';
import { resolveRetouchMode } from '@lite-workspace/dto-fotoclass-book';
import { ProjectsFacade } from '../features/projects/domain/application/projects.facade';
import { RETOUCH_FILTER_PARAM, RETOUCH_WAITING_FILTER, RetouchAccessService } from './retouch-access.service';
import { RetouchActivityService } from './retouch-activity.service';

/** `?batch=<token>` (the ids live in RetouchActivityService); the old `?files=a,b` address still opens. */
function editorFileIds(route: ActivatedRouteSnapshot, activity: RetouchActivityService): readonly string[] {
  const projectId = route.paramMap.get('projectId') ?? '';
  const batch = route.queryParamMap.get('batch');
  if (batch) return activity.batchIds(projectId, batch) ?? [];
  return route.queryParamMap.get('files')?.split(',').filter(Boolean) ?? [];
}

/**
 * The editor opens only with retouch on in the project and a known selection; otherwise the project's photos —
 * under «Ждут ретуши» when retouch is on (a stale token after a reload lands where the editor starts from).
 * The single-photo editor (`?files=<id>&single=1`) opens with retouch off too. A manual project lands on its section.
 * Without `?ai-retouch=1` the editor never opens.
 */
export const retouchEditorGuard: CanActivateFn = async route => {
  const router = inject(Router);
  const access = inject(RetouchAccessService);
  const activity = inject(RetouchActivityService);
  const facade = inject(ProjectsFacade);
  const projectId = route.paramMap.get('projectId');
  if (!projectId) return router.createUrlTree(['/projects']);
  const on = await access.load(projectId);
  // `on` = the settings are read: the mode is the project's own. Manual has no AI editor, batch or single.
  const manual = !access.aiEnabled || resolveRetouchMode(facade.baseSettingsByProject()[projectId]?.retouch) === 'manual';
  if (on && manual) {
    return router.createUrlTree(['/projects', projectId, 'retouch']);
  }

  if (!on && (!access.aiEnabled || !booleanAttribute(route.queryParamMap.get('single')))) {
    return router.createUrlTree(['/projects', projectId, 'photos']);
  }

  if (editorFileIds(route, activity).length === 0) {
    return router.createUrlTree(['/projects', projectId, 'photos'], {
      queryParams: { [RETOUCH_FILTER_PARAM]: RETOUCH_WAITING_FILTER },
    });
  }

  void activity.watch(projectId);
  return true;
};

/** Feeds the editor's `files` input (route data outranks the query in the input binding). */
export const retouchEditorFilesResolver: ResolveFn<string> = route =>
  editorFileIds(route, inject(RetouchActivityService)).join(',');
